import { z } from "zod";

/**
 * Per-option dispositions that determine participant eligibility.
 *
 * Storage tokens (unchanged for back-compat) map to the researcher-facing
 * vocabulary via SCREENING_ACTION_LABELS:
 * - MUST ("Must select"): this exact option must be selected to pass (NEW,
 *   multi-select only). Every MUST option in a question must be chosen.
 * - QUALIFY ("Must select one of"): at least one option in the question's
 *   qualifying group must be selected (the historical any-of semantics).
 * - NOT_IMPORTANT ("May select"): acceptable, no effect on eligibility.
 * - REJECT ("Reject"): selecting disqualifies.
 * - REVIEW ("Flag for review"): selecting neither auto-qualifies nor
 *   auto-rejects; it diverts an otherwise-qualified applicant to human review
 *   instead of being auto-invited. Only meaningful on AUTO_INVITE studies (on
 *   MANUAL_REVIEW every applicant is reviewed anyway); the builder offers it
 *   only there. Routes the submission to the customer review gate
 *   (AWAITING_CUSTOMER_REVIEW) via resolveInternalApprovalTarget (ENG-1223).
 *
 * `MUST` and `QUALIFY` are both "qualifying" dispositions; see
 * isQualifyingAction. New values appended (not renamed) so the many existing
 * readers/writers of the legacy tokens keep working with no data migration.
 */
export const screeningActionSchema = z.enum([
  "QUALIFY",
  "REJECT",
  "NOT_IMPORTANT",
  "MUST",
  "REVIEW",
]);

export type ScreeningAction = z.infer<typeof screeningActionSchema>;

/** Researcher-facing labels for the option dispositions. */
export const SCREENING_ACTION_LABELS: Record<ScreeningAction, string> = {
  MUST: "Must select",
  QUALIFY: "Must select one of",
  NOT_IMPORTANT: "May select",
  REJECT: "Reject",
  REVIEW: "Flag for review",
};

/**
 * True for dispositions that make an option count toward qualification (as
 * opposed to REJECT or the neutral MAY). Both the per-option MUST and the
 * any-of QUALIFY qualify; readers that historically checked `=== "QUALIFY"`
 * to mean "a qualifying option" should use this so MUST is included. REVIEW is
 * deliberately NOT qualifying because it defers the decision to a human.
 */
export function isQualifyingAction(action: string): boolean {
  return action === "QUALIFY" || action === "MUST";
}

/**
 * Collapse multi-select-only dispositions onto QUALIFY for single-select
 * questions: you can only pick one answer, so the per-option MUST and the
 * neutral NOT_IMPORTANT have no meaning there. Call at every write boundary
 * (save paths, AI parse, external-import) so a single-select MUST arriving from
 * any source (UI, copilot, or external API) is normalized the same way.
 */
export function normalizeActionForSelectionType(
  action: ScreeningAction,
  selectionType: SelectionType | undefined,
): ScreeningAction {
  if (
    selectionType === "SINGLE_SELECT" &&
    (action === "MUST" || action === "NOT_IMPORTANT")
  ) {
    return "QUALIFY";
  }
  return action;
}

export const quotaTypeSchema = z.enum(["EXACT", "MINIMUM", "MAXIMUM"]);

export const selectionTypeSchema = z.enum([
  "SINGLE_SELECT",
  "MULTI_SELECT",
  "OPEN_TEXT",
  // Matrix question: each row is a single-select across the shared columns.
  "GRID",
]);

export type SelectionType = z.infer<typeof selectionTypeSchema>;

export const screeningOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  action: screeningActionSchema,
  targetCount: z.number().int().min(0).optional(),
  quotaType: quotaTypeSchema.optional(),
  // "Other (please specify)" support on a select option. When true, selecting
  // this option reveals a free-text input the participant must fill in; the
  // typed value is captured on the answer's `freeText` field. The option still
  // carries a normal `action`, so eligibility evaluation is unchanged.
  allowFreeText: z.boolean().optional(),
});

export type ScreeningOption = z.infer<typeof screeningOptionSchema>;

// ---------------------------------------------------------------------------
// Grid (matrix) questions
// ---------------------------------------------------------------------------

export const screeningGridRowSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export const screeningGridColumnSchema = z.object({
  value: z.string(),
  label: z.string(),
});
/**
 * Disposition for a single grid cell, keyed by (rowId, columnValue). A cell
 * with no explicit action is treated as MAY (neutral). MUST / QUALIFY / REJECT
 * carry the same meaning as on a normal option, applied to the flattened cell.
 */
export const screeningGridCellActionSchema = z.object({
  rowId: z.string(),
  columnValue: z.string(),
  action: screeningActionSchema,
});

/**
 * How many columns a participant may choose per row:
 * - PICK_ONE (default): each row is a single choice across the columns (radio).
 * - PICK_ANY: each row allows any number of columns (checkbox).
 * Either way every row must be answered (at least one column). Absent =
 * PICK_ONE so every existing grid keeps its single-select behavior with no
 * data migration.
 */
export const gridSelectionModeSchema = z.enum(["PICK_ONE", "PICK_ANY"]);

export type GridSelectionMode = z.infer<typeof gridSelectionModeSchema>;

/**
 * A matrix question: `rows` down the side, shared `columns` across the top.
 * `selectionMode` decides whether each row takes one column (PICK_ONE) or any
 * number (PICK_ANY). Participant answers are stored as composite
 * "rowId:columnValue" values in the answer's `selectedValues`, so grids reuse
 * the existing answer shape with no schema change.
 */
export const screeningGridSchema = z.object({
  rows: z.array(screeningGridRowSchema),
  columns: z.array(screeningGridColumnSchema),
  cellActions: z.array(screeningGridCellActionSchema).optional(),
  selectionMode: gridSelectionModeSchema.optional(),
});

export type ScreeningGrid = z.infer<typeof screeningGridSchema>;
export type ScreeningGridCellAction = z.infer<
  typeof screeningGridCellActionSchema
>;

/** Composite id for a grid cell: the answer value and evaluation key. */
export function gridCellValue(rowId: string, columnValue: string): string {
  return `${rowId}:${columnValue}`;
}

/** A grid's selection mode, defaulting to PICK_ONE when unset (back-compat). */
export function gridSelectionMode(
  grid: Pick<ScreeningGrid, "selectionMode">,
): GridSelectionMode {
  return grid.selectionMode ?? "PICK_ONE";
}

/**
 * Whether a grid can never be passed by anyone, so a study built on it would
 * screen everyone out. An empty grid (no rows or no columns) always qualifies.
 *
 * Every row must be answered (at least one column), so in both modes a row
 * whose every cell is an explicit REJECT is impossible: any selection fails.
 * PICK_ONE (one cell per row) adds two more cases:
 * - a row with two-plus MUST cells (one choice can't satisfy both), and
 * - the grid has QUALIFY (any-of) cells but every row carrying one also carries
 *   a MUST cell, which pins that row to the MUST cell and away from the QUALIFY,
 *   so no participant can ever satisfy the required any-of. (PICK_ANY escapes
 *   both: a row can select its MUST cell and a QUALIFY cell together.)
 * Cells absent from cellActions are neutral (MAY).
 */
export function gridHasImpossibleRow(
  grid: Pick<
    ScreeningGrid,
    "rows" | "columns" | "cellActions" | "selectionMode"
  >,
): boolean {
  if (grid.rows.length === 0 || grid.columns.length === 0) {
    return true;
  }
  const pickOne = gridSelectionMode(grid) === "PICK_ONE";
  const rejected = new Set<string>();
  const mustCountByRow = new Map<string, number>();
  const rowsWithQualify = new Set<string>();
  for (const c of grid.cellActions ?? []) {
    if (c.action === "REJECT") {
      rejected.add(gridCellValue(c.rowId, c.columnValue));
    } else if (c.action === "MUST") {
      mustCountByRow.set(c.rowId, (mustCountByRow.get(c.rowId) ?? 0) + 1);
    } else if (c.action === "QUALIFY") {
      rowsWithQualify.add(c.rowId);
    }
  }
  // PICK_ONE: a QUALIFY can only be chosen in a row not pinned to a MUST. If a
  // required any-of group exists but no such row does, it can never be met.
  if (
    pickOne &&
    rowsWithQualify.size > 0 &&
    !grid.rows.some(
      (row) =>
        rowsWithQualify.has(row.id) && (mustCountByRow.get(row.id) ?? 0) === 0,
    )
  ) {
    return true;
  }
  return grid.rows.some(
    (row) =>
      (pickOne && (mustCountByRow.get(row.id) ?? 0) >= 2) ||
      grid.columns.every((col) =>
        rejected.has(gridCellValue(row.id, col.value)),
      ),
  );
}

/** A fresh grid with one blank row and two blank columns (builder default). */
export function createEmptyScreeningGrid(): ScreeningGrid {
  return {
    rows: [{ id: crypto.randomUUID(), label: "" }],
    columns: [
      { value: crypto.randomUUID().slice(0, 8), label: "" },
      { value: crypto.randomUUID().slice(0, 8), label: "" },
    ],
    cellActions: [],
  };
}

export const skipConditionSchema = z.object({
  // Source question whose answer this condition tests. Absent = the question
  // the condition lives on (back-compat: skip rules default to their own
  // question). Display conditions always set it to a prior question.
  questionId: z.string().optional(),
  optionValue: z.string(),
  operator: z.enum(["==", "!="]),
});

export type SkipCondition = z.infer<typeof skipConditionSchema>;

export const skipRuleSchema = z.object({
  conditions: z.array(skipConditionSchema).min(1).max(26),
  joinMethod: z.enum(["and", "or"]),
  targetQuestionId: z.string().nullable(),
});

export type SkipRule = z.infer<typeof skipRuleSchema>;

/**
 * "Ask only if" gate on a question. When present, the question is shown to the
 * participant only when the conditions (evaluated against earlier answers)
 * hold. Reuses skipConditionSchema, whose conditions carry a source
 * `questionId` pointing at a prior question.
 */
export const displayConditionSchema = z.object({
  conditions: z.array(skipConditionSchema).min(1).max(26),
  joinMethod: z.enum(["and", "or"]),
});

export type DisplayCondition = z.infer<typeof displayConditionSchema>;

/**
 * Conditional consistency rule (ENG-1223 B3): a cross-question logic check that
 * fires a REJECT or REVIEW when the participant's answers match a pattern - e.g.
 * "if Q4.role is Store Manager AND none of the management tasks are selected here,
 * flag for review". Reuses skipConditionSchema, so each condition tests whether an
 * option is selected (`==`) or not (`!=`) on a referenced question (`questionId`,
 * which may point at an earlier question OR this one for a self-check). Conditions
 * are joined by `joinMethod`; when the rule matches, `outcome` applies (REJECT
 * screens out, REVIEW flags for a human). This is the anti-falsification /
 * role-consistency mechanic: the qualifying set is expressed as a rule over an
 * earlier answer rather than a static per-option disposition. Evaluated by the
 * form-screening evaluator after the standard per-option checks.
 */
export const conditionalRuleSchema = z.object({
  conditions: z.array(skipConditionSchema).min(1).max(26),
  joinMethod: z.enum(["and", "or"]),
  outcome: z.enum(["REJECT", "REVIEW"]),
});

export type ConditionalRule = z.infer<typeof conditionalRuleSchema>;

/**
 * Whether a conditional rule matches the participant's answers. Each condition
 * tests one option on a referenced question: `==` holds when that option is among
 * the selected values, `!=` when it is absent (so an unanswered/skipped source
 * reads as "not selected"). `questionId` defaults to `selfQuestionId` for a
 * same-question check ("none of these selected here"). AND requires every
 * condition; OR any. Pure boolean over selections - counts/thresholds are B1/B4,
 * not here.
 */
export function matchesConditionalRule(
  rule: Pick<ConditionalRule, "conditions" | "joinMethod">,
  answers: Record<string, string[]>,
  selfQuestionId: string,
): boolean {
  // A rule with no conditions must never fire: a vacuous `and` (.every -> true)
  // would otherwise reject or flag every participant.
  if (rule.conditions.length === 0) {
    return false;
  }
  const ownSelected = answers[selfQuestionId] ?? [];
  const holds = (c: SkipCondition): boolean => {
    const sourceId = c.questionId ?? selfQuestionId;
    // A cross-question source the participant never answered - gated by its own
    // "ask only if", skipped, later in the form, or since deleted - cannot
    // satisfy a consistency condition. Return false instead of letting
    // evaluateScreeningCondition read the missing answer as an empty selection,
    // which would make a `!=` condition fire spuriously and reject/flag people
    // who never saw the source. The self question is always present when its own
    // rules run (even with an empty selection), so self conditions still eval.
    if (sourceId !== selfQuestionId && !(sourceId in answers)) {
      return false;
    }
    return evaluateScreeningCondition(c, ownSelected, answers);
  };
  return rule.joinMethod === "and"
    ? rule.conditions.every(holds)
    : rule.conditions.some(holds);
}

export const screeningQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  // Optional rich-text prompt, stored as Markdown (GFM). When present it is the
  // authoritative display copy (renders bold, bullet lists, etc.); `question`
  // remains the derived plaintext source of truth for the voice agent, external
  // API, search, and any consumer that cannot render Markdown. Absent on
  // pre-existing questions, which simply render `question` as before.
  questionRichText: z.string().optional(),
  options: z.array(screeningOptionSchema),
  // GRID only: the matrix structure (rows, columns, cell dispositions). GRID
  // questions carry an empty `options` array and put everything here.
  grid: screeningGridSchema.optional(),
  insightId: z.string().optional(),
  selectionType: selectionTypeSchema.optional(),
  // Count-threshold qualification (ENG-1223 B1): on a MULTI_SELECT, require the
  // applicant to select at least this many of the QUALIFY ("must select one of")
  // options - e.g. "accept if 2+ relevant metrics". Absent or 1 = the existing
  // "must select one of" behavior (any one qualifies), so it is fully backward
  // compatible with no migration. Ignored on non-MULTI questions (single-select
  // can only pick one). See questionQualifyThreshold.
  qualifyThreshold: z.number().int().min(1).optional(),
  skipRules: z.array(skipRuleSchema).optional(),
  // "Ask only if" gate: when set, the question is shown only when the condition
  // over earlier answers holds. The backend evaluator owns this; it is stripped
  // before shipping to the client.
  displayCondition: displayConditionSchema.optional(),
  // Conditional consistency rules (ENG-1223 B3): cross-question REJECT/REVIEW
  // checks that fire when answers match a role-inconsistency pattern. See
  // conditionalRuleSchema / matchesConditionalRule.
  conditionalRules: z.array(conditionalRuleSchema).optional(),
  // OPEN_TEXT only. Paste is blocked by default on free-text answers to
  // discourage low-effort copied responses; opt in here for questions that
  // ask for a pasted value (e.g. a direct link).
  allowPaste: z.boolean().optional(),
  // Participant-facing, leak-safe reason shown on screen-out, written by the
  // generate-screen-out-reasons task. Absent until then; UI shows generic copy.
  screenOutReason: z.string().optional(),
});

export type ScreeningQuestion = z.infer<typeof screeningQuestionSchema>;

/**
 * Assign every question a unique, stable id so no two questions in a guide share
 * one. A missing/blank id, or an id already used by an EARLIER question in the
 * array, is replaced with a fresh uuid; the FIRST occurrence of each id keeps
 * it.
 *
 * Question ids are ultimately LLM-supplied (the AI parser and the study copilot
 * both hand the model a "q1, q2, ..." scheme, which a model can and does
 * repeat), and every reader resolves a question id by FIRST match: answers are
 * stored keyed by questionId, and skip targets / display gates find their
 * question with `find(q => q.id === ...)`. A duplicate id therefore makes the
 * later question share the first's answer bucket, so it can never be answered
 * and the next-question walker demands it forever, wedging the whole screener.
 *
 * Keeping the FIRST occurrence's id is what makes this safe for references:
 * every existing skipRules.targetQuestionId / displayCondition.questionId
 * already resolves to the first occurrence, so it keeps pointing exactly where
 * it did - only the (un-referenceable) later duplicate is renamed, so nothing
 * needs re-pointing. Call this at every screening-guide write boundary, the
 * single convergence point for the copilot, AI parser, manual editor, and
 * external API.
 *
 * A kept id is preserved VERBATIM (only blank or duplicate ids are reassigned):
 * callers may have already resolved cross-question references against the raw
 * id (the external API binds displayCondition/quota refs to the caller's key),
 * so transforming a valid id here (e.g. trimming whitespace) would desync those
 * references. `trim()` is used ONLY to detect a blank id, never to rewrite one.
 */
export function assignUniqueScreeningQuestionIds<
  T extends { id?: string | null },
>(questions: T[]): (Omit<T, "id"> & { id: string })[] {
  const usedIds = new Set<string>();
  return questions.map((q) => {
    const raw = q.id ?? "";
    const keepable = raw.trim().length > 0 && !usedIds.has(raw);
    const id = keepable ? raw : crypto.randomUUID();
    usedIds.add(id);
    return { ...q, id };
  });
}

/**
 * How many QUALIFY ("must select one of") options a question requires selected
 * (ENG-1223 B1 count-threshold). Defaults to 1 - the legacy "any one of the
 * group qualifies" behavior. A threshold > 1 is only meaningful on MULTI_SELECT
 * (single-select can pick just one), so single-select always reads as 1.
 */
export function questionQualifyThreshold(
  question: Pick<ScreeningQuestion, "selectionType" | "qualifyThreshold">,
): number {
  if (question.selectionType === "SINGLE_SELECT") {
    return 1;
  }
  return question.qualifyThreshold ?? 1;
}

/**
 * A count threshold the question can actually satisfy: never more than the
 * number of qualifying answers it still carries (QUALIFY options, or QUALIFY
 * cells on a grid).
 *
 * A threshold above that count is unsatisfiable, and the evaluator turns it
 * into a blanket REJECT (`qualifyCount < qualifyThreshold` in
 * form-screening-evaluator.ts) - the question screens out every applicant. It is
 * never authored that way; it is what an editor that can delete an option
 * without touching the threshold leaves behind, so clamp at the write boundary
 * rather than trusting each client to (ScreenerModal clamps its own control,
 * but the live screening editor has no threshold control at all).
 *
 * Returns undefined when the threshold collapses to the default 1, so writers
 * omit the field instead of storing a redundant value.
 */
export function clampQualifyThreshold(
  question: Pick<
    ScreeningQuestion,
    "selectionType" | "options" | "grid" | "qualifyThreshold"
  >,
): number | undefined {
  if (question.qualifyThreshold == null) {
    return undefined;
  }
  const qualifyingCount =
    question.selectionType === "GRID"
      ? (question.grid?.cellActions ?? []).filter((c) => c.action === "QUALIFY")
          .length
      : question.options.filter((o) => o.action === "QUALIFY").length;
  const clamped = Math.min(
    Math.max(question.qualifyThreshold, 1),
    Math.max(qualifyingCount, 1),
  );
  return clamped > 1 ? clamped : undefined;
}

/**
 * Every answer value a question can produce: flat option values plus the
 * composite "rowId:columnValue" of each grid cell. The grid is passed in rather
 * than read off the question because writers resolve it themselves (an incoming
 * patch may omit the matrix and fall back to the stored one).
 */
export function screeningQuestionAnswerValues(question: {
  options: { value: string }[];
  grid?: Pick<ScreeningGrid, "rows" | "columns">;
}): Set<string> {
  const values = new Set(question.options.map((o) => o.value));
  for (const row of question.grid?.rows ?? []) {
    for (const col of question.grid?.columns ?? []) {
      values.add(gridCellValue(row.id, col.value));
    }
  }
  return values;
}

/**
 * Drop consistency rules that reference an answer which no longer exists, on
 * this question (a self-condition) or on the question they point at.
 *
 * Drop the WHOLE rule, not the dangling condition: a stale `!=` on a deleted
 * option is true for every participant, and stripping it from an AND rule
 * ("role == X AND none of these tasks") leaves the bare "role == X" gate that
 * fires for everyone of that role. Either way an ordinary option deletion turns
 * a consistency check into an unconditional reject, so a rule that can no longer
 * mean what it said must not survive the save.
 *
 * Shared by every screening-guide writer - the study-draft save, the live
 * options update, and the internal full rewrite - so a guard against
 * mass-rejecting a live study cannot hold in one path and not the others.
 */
export function dropDriftedConditionalRules(
  rules: ConditionalRule[] | undefined,
  ownAnswerValues: Set<string>,
  answerValuesByQuestionId: Map<string, Set<string>>,
): ConditionalRule[] | undefined {
  return rules?.filter((rule) =>
    rule.conditions.every((condition) => {
      const sourceValues = condition.questionId
        ? answerValuesByQuestionId.get(condition.questionId)
        : ownAnswerValues;
      return sourceValues?.has(condition.optionValue) ?? false;
    }),
  );
}

/**
 * The qualifying (QUALIFY or MUST) and rejecting answer labels for a question,
 * covering both flat options and GRID cells (grids carry their dispositions on
 * grid.cellActions and keep options empty). A grid cell reads as "Row: Column",
 * falling back to the raw id when a row/column can't be resolved.
 *
 * Shared by the recruitment matcher, referral listing, and screen-out reason
 * generation so grid + MUST handling cannot drift between those readers.
 */
export function screeningQuestionAnswerLabels(
  question: Pick<ScreeningQuestion, "selectionType" | "options" | "grid">,
): { qualify: string[]; reject: string[] } {
  if (question.selectionType === "GRID" && question.grid) {
    const rowLabel = new Map(question.grid.rows.map((r) => [r.id, r.label]));
    const colLabel = new Map(
      question.grid.columns.map((c) => [c.value, c.label]),
    );
    const qualify: string[] = [];
    const reject: string[] = [];
    for (const cell of question.grid.cellActions ?? []) {
      const label = `${rowLabel.get(cell.rowId) ?? cell.rowId}: ${
        colLabel.get(cell.columnValue) ?? cell.columnValue
      }`;
      if (isQualifyingAction(cell.action)) {
        qualify.push(label);
      } else if (cell.action === "REJECT") {
        reject.push(label);
      }
    }
    return { qualify, reject };
  }
  return {
    qualify: question.options
      .filter((o) => isQualifyingAction(o.action))
      .map((o) => o.label),
    reject: question.options
      .filter((o) => o.action === "REJECT")
      .map((o) => o.label),
  };
}

/**
 * Evaluate a single condition against previously-collected answers. A condition
 * with a `questionId` tests that question's selected values; without one it
 * falls back to `ownSelected` (the answers to the question the condition lives
 * on, used by skip rules). `==` means "the option is selected", `!=` its negation.
 */
export function evaluateScreeningCondition(
  condition: SkipCondition,
  ownSelected: string[],
  answersByQuestionId: Record<string, string[]>,
): boolean {
  const selected = condition.questionId
    ? (answersByQuestionId[condition.questionId] ?? [])
    : ownSelected;
  const has = selected.includes(condition.optionValue);
  return condition.operator === "==" ? has : !has;
}

/**
 * Whether a question should be shown, given the answers collected so far. A
 * question with no `displayCondition` is always shown; otherwise its conditions
 * (which reference earlier questions by `questionId`) are joined by and/or.
 *
 * Resolved server-side only (form-screening-evaluator.ts and
 * get-screening-steps.ts); gated questions never reach the client.
 */
export function isScreeningQuestionVisible(
  question: Pick<ScreeningQuestion, "displayCondition">,
  answersByQuestionId: Record<string, string[]>,
): boolean {
  const dc = question.displayCondition;
  if (!dc || dc.conditions.length === 0) {
    return true;
  }
  const results = dc.conditions.map((c) =>
    evaluateScreeningCondition(c, [], answersByQuestionId),
  );
  return dc.joinMethod === "and"
    ? results.every(Boolean)
    : results.some(Boolean);
}

/**
 * Advance from `index` past any question hidden by its "ask only if"
 * displayCondition, given the answers collected so far. Returns the first
 * visible question index at or after `index`, or null when the walk runs off
 * the end (screener complete).
 */
export function skipHiddenScreeningQuestions<
  Q extends Pick<ScreeningQuestion, "displayCondition">,
>(
  index: number,
  allQuestions: Q[],
  answersByQuestionId: Record<string, string[]>,
): number | null {
  let i = index;
  while (i < allQuestions.length) {
    const q = allQuestions[i];
    if (q === undefined || isScreeningQuestionVisible(q, answersByQuestionId)) {
      break;
    }
    i += 1;
  }
  return i < allQuestions.length ? i : null;
}

function evaluateScreeningSkipRule(
  rule: SkipRule,
  ownSelected: string[],
  answersByQuestionId: Record<string, string[]>,
): boolean {
  const results = rule.conditions.map((c) =>
    evaluateScreeningCondition(c, ownSelected, answersByQuestionId),
  );
  return rule.joinMethod === "and"
    ? results.every(Boolean)
    : results.some(Boolean);
}

/**
 * Determine the next question index from a question's forward skip rules, then
 * skip past any question hidden by its displayCondition. Returns null to end
 * the screener, or the next visible question index.
 *
 * The single source of truth for screener navigation. Used by the backend
 * evaluator (form-screening-evaluator.ts) and the step builder
 * (get-screening-steps.ts); the participant only ever receives the step it is
 * meant to answer, so "what the participant sees" and "what the evaluator
 * scores" cannot drift. (screening_evaluator.py is a separate Python port that
 * does not implement displayCondition / cross-question conditions.)
 */
export function resolveNextScreeningQuestion<
  Q extends {
    id: string;
    skipRules?: SkipRule[];
    displayCondition?: DisplayCondition;
  },
>(
  question: Q,
  ownSelected: string[],
  allQuestions: Q[],
  currentIndex: number,
  answersByQuestionId: Record<string, string[]>,
): number | null {
  let next = currentIndex + 1;
  for (const rule of question.skipRules ?? []) {
    if (evaluateScreeningSkipRule(rule, ownSelected, answersByQuestionId)) {
      if (rule.targetQuestionId === null) {
        return null;
      }
      const targetIdx = allQuestions.findIndex(
        (q) => q.id === rule.targetQuestionId,
      );
      if (targetIdx > currentIndex) {
        next = targetIdx;
        break;
      }
    }
  }
  return skipHiddenScreeningQuestions(next, allQuestions, answersByQuestionId);
}

/**
 * Every answer value -> label pair for a question: flat options plus grid cells
 * (composite "rowId:columnValue" -> "Row: Column"). Callers key their lookup by
 * `${questionId}:${value}`, so the question-id prefix is caller-specific and not
 * included here. Shared by the screening-answer table/result readers so the
 * grid-cell labelling cannot drift between them.
 */
export function screeningAnswerValueLabels(
  question: Pick<ScreeningQuestion, "options" | "grid">,
): { value: string; label: string }[] {
  const pairs = question.options.map((o) => ({
    value: o.value,
    label: o.label,
  }));
  for (const row of question.grid?.rows ?? []) {
    for (const col of question.grid?.columns ?? []) {
      pairs.push({
        value: gridCellValue(row.id, col.value),
        label: `${row.label}: ${col.label}`,
      });
    }
  }
  return pairs;
}

/**
 * Whether any question in the screener branches, i.e. carries a forward skip
 * rule or an "ask only if" display condition. Shared so the backend evaluator,
 * the get-screening-steps disclosure bit, and the participant card agree on
 * when the remaining question count is dynamic rather than linear.
 */
export function screenerHasConditionalNavigation(
  questions: Pick<ScreeningQuestion, "skipRules" | "displayCondition">[],
): boolean {
  return questions.some(
    (q) => (q.skipRules?.length ?? 0) > 0 || Boolean(q.displayCondition),
  );
}

export const screeningGuideSchema = z.object({
  rawInput: z.string(),
  questions: z.array(screeningQuestionSchema),
  estimatedDurationMins: z.number().int().min(1).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ScreeningGuide = z.infer<typeof screeningGuideSchema>;

/**
 * Preview format returned by AI parsing before creating UserInsights
 */
export const parsedScreeningQuestionsSchema = z.object({
  // Same shape as an authored question minus the fields the AI parser never
  // produces (assigned later); derived from the canonical schema so the two
  // cannot drift as question fields evolve.
  questions: z.array(
    screeningQuestionSchema.omit({ insightId: true, screenOutReason: true }),
  ),
});

export type ParsedScreeningQuestions = z.infer<
  typeof parsedScreeningQuestionsSchema
>;

/**
 * Skip-rule reachability.
 *
 * The screener runtime (`resolveNextScreeningQuestion` above, and the synced
 * Python port in screening_evaluator.py) only honors FORWARD
 * jumps: a skip rule fires only when its target sits strictly after the source
 * question. A target at or before the source is silently ignored, so after a
 * drag-and-drop reorder a previously-valid "go to question" can quietly become
 * a no-op. Editors use these helpers to detect and surface that case.
 */
export function isSkipTargetUnreachable(
  targetQuestionId: string | null,
  sourceIndex: number,
  orderedQuestionIds: string[],
): boolean {
  // null = "end the screener", which is always reachable.
  if (targetQuestionId === null) {
    return false;
  }
  const targetIndex = orderedQuestionIds.indexOf(targetQuestionId);
  // Unknown target (e.g. a deleted question) is left to other validation; only
  // flag a target that resolves to a position at or before the source.
  return targetIndex !== -1 && targetIndex <= sourceIndex;
}

/**
 * True when any of the question's skip rules points at a target that the
 * forward-only runtime can no longer reach from its current position.
 */
export function questionHasUnreachableSkip(
  skipRules: Pick<SkipRule, "targetQuestionId">[] | undefined,
  sourceIndex: number,
  orderedQuestionIds: string[],
): boolean {
  return (skipRules ?? []).some((rule) =>
    isSkipTargetUnreachable(
      rule.targetQuestionId,
      sourceIndex,
      orderedQuestionIds,
    ),
  );
}

/** Minimal question shape a condition can point at (option or grid cell). */
export type ConditionSourceQuestion = Pick<
  ScreeningQuestion,
  "id" | "options" | "grid"
>;

/**
 * Human-readable label for a condition's answer value on its source question,
 * or null when the value no longer exists (deleted option / grid cell). Grid
 * cells are the composite "rowId:columnValue" and render as "Row: Column".
 */
export function screeningConditionValueLabel(
  question: Pick<ScreeningQuestion, "options" | "grid"> | undefined,
  optionValue: string,
): string | null {
  if (!question) {
    return null;
  }
  const opt = question.options.find((o) => o.value === optionValue);
  if (opt) {
    return opt.label || opt.value;
  }
  const grid = question.grid;
  if (grid) {
    // Match against the grid's known "rowId:columnValue" composites rather than
    // splitting on the first colon: a row id can itself contain a colon
    // (external API row keys), which a slice-on-first-colon parse would break.
    for (const row of grid.rows) {
      for (const col of grid.columns) {
        if (gridCellValue(row.id, col.value) === optionValue) {
          return `${row.label || "row"}: ${col.label || "column"}`;
        }
      }
    }
  }
  return null;
}

/**
 * Display-condition validity.
 *
 * An "ask only if" gate can only be evaluated against answers already
 * collected, so every condition must reference a question that sits strictly
 * BEFORE the gated one AND an answer value that still exists on it. A condition
 * pointing at a later/self/deleted question, or at an answer option/grid cell
 * that was since removed, can never be satisfied as intended and would silently
 * hide the question forever. Editors and launch validation use this to surface
 * the broken reference.
 */
export function questionHasInvalidDisplayCondition(
  displayCondition: DisplayCondition | undefined,
  sourceIndex: number,
  orderedQuestions: ConditionSourceQuestion[],
): boolean {
  if (!displayCondition) {
    return false;
  }
  return displayCondition.conditions.some((c) => {
    if (!c.questionId) {
      // A display condition with no source question can never resolve against
      // an earlier answer, so it is invalid.
      return true;
    }
    const refIndex = orderedQuestions.findIndex((q) => q.id === c.questionId);
    if (refIndex === -1 || refIndex >= sourceIndex) {
      return true;
    }
    // The referenced answer value must still exist on the source question.
    return (
      screeningConditionValueLabel(
        orderedQuestions[refIndex],
        c.optionValue,
      ) === null
    );
  });
}

/**
 * Conditional-rule validity (ENG-1223 B3).
 *
 * A consistency rule ("reject / flag if …") tests option values on THIS question
 * (a self-condition: questionId omitted, or equal to this question) or on an
 * EARLIER question. Unlike a display gate its outcome is REJECT/REVIEW, so a
 * dangling reference fails in the dangerous direction: a `!=` condition on an
 * option that no longer exists is true for every participant, turning a
 * consistency check into an unconditional reject. A condition is broken when a
 * cross-question source is missing or not strictly earlier, or when its answer
 * option / grid cell was since removed (on the self question or the referenced
 * one). The save cleaners drop such rules; launch validation uses this to
 * surface any that slipped through so a broken rule can't silently mass-reject.
 */
export function questionHasInvalidConditionalRule(
  conditionalRules: ConditionalRule[] | undefined,
  sourceIndex: number,
  orderedQuestions: ConditionSourceQuestion[],
): boolean {
  if (!conditionalRules?.length) {
    return false;
  }
  const selfQuestion = orderedQuestions[sourceIndex];
  return conditionalRules.some((rule) =>
    rule.conditions.some((c) => {
      // Self-check (no questionId, or an explicit ref to this question): the
      // referenced option / grid cell must still exist on this question.
      if (!c.questionId || c.questionId === selfQuestion?.id) {
        return (
          screeningConditionValueLabel(selfQuestion, c.optionValue) === null
        );
      }
      // Cross-question: the source must be a question that sits strictly BEFORE
      // this one and still carries the referenced answer value.
      const refIndex = orderedQuestions.findIndex((q) => q.id === c.questionId);
      if (refIndex === -1 || refIndex >= sourceIndex) {
        return true;
      }
      return (
        screeningConditionValueLabel(
          orderedQuestions[refIndex],
          c.optionValue,
        ) === null
      );
    }),
  );
}

/**
 * The question shape the launch gate reads, widened past `ScreeningQuestion` so
 * both of its callers fit: the builder's own question interface leaves `id`
 * optional and allows an explicit null `displayCondition`, and a guide read back
 * off the `Study.screeningGuide` JSONB column may predate a field entirely.
 */
export type LaunchGateQuestion = Pick<
  ScreeningQuestion,
  "grid" | "selectionType" | "conditionalRules"
> & {
  id?: string;
  options?: ScreeningOption[];
  displayCondition?: DisplayCondition | null;
};

export const UNPASSABLE_QUESTION_MESSAGE =
  "Every screening question needs an answer that isn't Reject, so someone can pass";

/**
 * Whether someone can pass a screening question, the rule behind both the
 * launch gate and the live editors' Save. A select is impossible only when every
 * option REJECTs (or it has none); this matches the screening engine
 * (services/submission/form-screening-evaluator.ts), which only *requires* a
 * QUALIFY pick when the question actually has a QUALIFY option and otherwise
 * passes any non-reject answer. So a multi-select of all-Neutral, or
 * Neutral+Reject, is passable.
 *
 * A grid is impossible when it has no matrix, or any single row is entirely
 * REJECT (every row must be answered, so any pick fails); gridHasImpossibleRow
 * is mode-aware. An ALL-NEUTRAL grid qualifies everyone, which is valid data
 * collection (a frequency matrix), exactly like an all-neutral select.
 * OPEN_TEXT is gated by manual review, so it always passes.
 *
 * Runs on untyped JSONB: a stored question with no `options` at all cannot be
 * answered, so it reads as impossible rather than throwing.
 */
export function questionCanBePassed(
  question: Pick<LaunchGateQuestion, "selectionType" | "options" | "grid">,
): boolean {
  if (question.selectionType === "OPEN_TEXT") {
    return true;
  }
  if (question.selectionType === "GRID") {
    return question.grid != null && !gridHasImpossibleRow(question.grid);
  }
  return (question.options ?? []).some((o) => o.action !== "REJECT");
}

/**
 * Returns a human reason the study can't launch yet on screening grounds, or
 * null when the screening guide is valid: at least one screening question, and
 * every non-open-text question must be passable by someone.
 *
 * Lives here rather than in the builder because it is the launch rule for BOTH
 * surfaces. The dashboard calls it to grey out its own button; the server calls
 * it in StudyLifecycleService.launchStudy to actually enforce it, because a
 * disabled button is no gate at all against a stale tab, a scripted call, or the
 * external API. One function so the two can never drift into two rules.
 */
export function screeningLaunchIssue(
  screeningGuide: { questions?: LaunchGateQuestion[] } | null | undefined,
): string | null {
  const questions = screeningGuide?.questions;
  // The launch gate passes the stored JSONB, so a malformed list must read as
  // no questions rather than throw a refusal out as a 500.
  if (!Array.isArray(questions) || questions.length === 0) {
    return "Add at least one screening question";
  }
  const impossibleToPass = questions.some(
    (q) => q == null || !questionCanBePassed(q),
  );
  if (impossibleToPass) {
    return UNPASSABLE_QUESTION_MESSAGE;
  }

  // A "show only if" gate can only reference answers already collected, so each
  // condition must point at an earlier question AND an answer that still exists.
  // A broken reference would hide the question from every participant forever.
  const orderedQuestions = questions.map((q, i) => ({
    id: q.id ?? `__idx_${i}`,
    options: q.options ?? [],
    grid: q.grid,
  }));
  const hasBrokenGate = questions.some((q, i) =>
    questionHasInvalidDisplayCondition(
      q.displayCondition ?? undefined,
      i,
      orderedQuestions,
    ),
  );
  if (hasBrokenGate) {
    return 'A "show this question only if" condition points at a question or answer that no longer works';
  }

  // A consistency rule fires REJECT/REVIEW, so a dangling reference is worse than
  // a broken gate: a `!=` on a deleted option is true for everyone, turning the
  // check into an unconditional reject. Block launch until it's fixed. Save
  // cleaners already drop these, but this is the backstop for a guide that
  // reached launch with a stale cross-question reference.
  const hasBrokenRule = questions.some((q, i) =>
    questionHasInvalidConditionalRule(
      q.conditionalRules ?? undefined,
      i,
      orderedQuestions,
    ),
  );
  if (hasBrokenRule) {
    return 'A consistency check ("reject or flag if…") points at a question or answer that no longer works';
  }

  return null;
}
